import * as React from "react";
import { applyMode as applyCloudscapeMode, Mode as CloudscapeMode } from "@cloudscape-design/global-styles";
import { applyMode as applyAwsuiMode, Mode as AwsuiMode } from "@amzn/awsui-global-styles";
import { useDebug } from "@amzn/awsmp-max-agentic-renderer";

export interface ThemeProviderProps {
  children: React.ReactNode;
}

/**
 * ThemeProvider - Wraps application content with theme support
 *
 * Responsibilities:
 * 1. Applies awsui-visual-refresh class for all design systems
 * 2. Manages dark mode based on debug settings
 *
 * Supports all design systems in the application:
 * - Public Cloudscape (@cloudscape-design/components)
 * - Internal Cloudscape/Polaris (@amzn/awsui-components-react/polaris/*)
 * - AWS-UI Global Styles (@amzn/awsui-global-styles)
 *
 * Applies mode to both Cloudscape and AWS-UI to ensure consistent theming.
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { settings } = useDebug();

  // Apply dark mode based on debug settings to both design systems
  React.useEffect(() => {
    if (settings.darkMode) {
      // When dark mode is enabled, respect system preference
      if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
        applyCloudscapeMode(CloudscapeMode.Dark);
        applyAwsuiMode(AwsuiMode.Dark);
      } else {
        applyCloudscapeMode(CloudscapeMode.Light);
        applyAwsuiMode(AwsuiMode.Light);
      }
    } else {
      // When dark mode is disabled, always use light mode
      applyCloudscapeMode(CloudscapeMode.Light);
      applyAwsuiMode(AwsuiMode.Light);
    }
  }, [settings.darkMode]);

  return <div>{children}</div>;
};
